import { createActiveOrderIndexFirstPart, createBestActiveOrderIndexFirstPart, createBestOrderIndexFirstPart, createMakerDepositFirstPart, createOrderByMakerIndexFirstPart, createOrderStateKey, createRequestByMarketMakerIndexFirstPart, createRequestKey, createL1TxInfoFirstPart, createRefundIndexFirstPart, createRefundByMarketMakerIndexFirstPart, createActiveOrderByMakerIndexFirstPart, } from './shared';
/**
 * Fetches a single C2 market maker order by its unique identifier.
 *
 * This function retrieves the complete order state from the offchain contract,
 * including all order details such as amounts, collateral, expiration dates,
 * and activity status.
 *
 * @param client - The offchain client instance used to fetch claims from the contract.
 *   Must have a `fetchClaims` method that queries the contract's claim storage.
 * @param id - The unique identifier of the order to retrieve.
 *
 * @returns A Promise that resolves to an {@link Order} object containing:
 *   - `id`: The order identifier
 *   - `baseAmount`: Base currency amount (BigInt)
 *   - `l1Amount`: L1 token amount (BigInt)
 *   - `minL1Amount`: Minimum acceptable L1 amount (BigInt)
 *   - `collateral`: Collateral amount (BigInt)
 *   - `covering`: Remaining amount to be covered (BigInt)
 *   - `createdAt`: Timestamp when the order was created
 *   - `expirationDate`: Timestamp when the order expires
 *   - `activityStatus`: Current activity status of the order
 *   - `marketMaker`: User who created the order
 *   - `baseRecipient`: User who will receive the base currency
 *   - `txId`: Transaction ID that created the order
 *   - `history`: History access information
 *
 * @throws {Error} Throws an error with message "C2 order state not found" if
 *   the order with the given ID does not exist in the contract.
 *
 * @example
 * ```typescript
 * import { type Client } from '@pactswap/cwap-cm-lib/offchain';
 * import { getOrderById } from 'market-maker.cm';
 *
 * const client: Client = {
 *   fetchClaims: async (firstPart, secondPart) => {
 *     // Implementation using a client library like '@coinweb/wallet-lib' or '@coinweb/claims-client'
 *   }
 * };
 *
 * enum ORDER_ACTIVITY_STATUS {
 *   ACTIVE = 'ACTIVE',
 *   PENDING = 'PENDING',
 *   DISABLED = 'DISABLED',
 * };
 *
 * type User = {
 *   auth: 'EcdsaContract';
 *   payload: string;
 * };
 *
 * type HistoryAccess = {
 *   historyId: string;
 *   timestamp?: number;
 * };
 *
 * type Order = {
 *   id: string;
 *   baseAmount: bigint;
 *   l1Amount: bigint;
 *   minL1Amount: bigint;
 *   collateral: bigint;
 *   covering: bigint;
 *   createdAt: number;
 *   expirationDate: number;
 *   activityStatus: ORDER_ACTIVITY_STATUS;
 *   marketMaker: User;
 *   baseRecipient: User;
 *   txId: string;
 *   history: HistoryAccess;
 * };
 *
 * // Fetch a specific order
 * const order: Order = await getOrderById(client, 'order-123');
 * console.log(`Order ${order.id} has ${order.baseAmount} base amount`);
 * ```
 */
export const getOrderById = async (client, id) => {
    const key = createOrderStateKey(id);
    const claimResponse = (await client.fetchClaims(key.first_part, key.second_part))[0];
    if (!claimResponse) {
        throw new Error('C2 order state not found');
    }
    const data = claimResponse.content.body;
    return {
        ...data,
        id,
        baseAmount: BigInt(data.baseAmount),
        l1Amount: BigInt(data.l1Amount),
        minL1Amount: BigInt(data.minL1Amount),
        collateral: BigInt(data.collateral),
        covering: BigInt(data.covering),
    };
};
/**
 * Fetches a single execution request (PACT) by its unique identifier.
 *
 * An execution request (also called a PACT) represents a user's request to
 * execute a swap against a market maker order. This function retrieves the
 * complete state of the execution request, including execution status,
 * amounts, chain data, and transaction information.
 *
 * @param client - The offchain client instance used to fetch claims from the contract.
 *   Must have a `fetchClaims` method that queries the contract's claim storage.
 * @param id - The unique identifier of the execution request (PACT) to retrieve.
 *
 * @returns A Promise that resolves to an object containing:
 *   - `id`: The execution request identifier
 *   - `baseAmount`: Base currency amount (BigInt)
 *   - `promisedL1Amount`: L1 amount promised by the market maker (BigInt)
 *   - `pactL1Amount`: Actual L1 amount in the PACT (BigInt)
 *   - `minL1Amount`: Minimum acceptable L1 amount (BigInt)
 *   - `collateral`: Collateral amount (BigInt)
 *   - `createdAt`: Timestamp when the request was created
 *   - `expirationDate`: Timestamp when the request expires
 *   - `executionStatus`: Current execution status of the request
 *   - `l1Recipient`: Address that will receive L1 tokens
 *   - `requestedOrderId`: ID of the order this request is for
 *   - `txId`: Transaction ID that created the request
 *   - `chainData`: Chain-specific data (e.g., BTC address and nonce)
 *   - `history`: History access information
 *   - `requestInfo`: Additional request information
 *   - `executionInfo`: Execution details including L1 transaction ID and amount
 *
 * @throws {Error} Throws an error with message "Claim not found" if the
 *   execution request with the given ID does not exist in the contract.
 *
 * @example
 * ```typescript
 * import { typeClient } from '@pactswap/cwap-cm-lib/offchain';
 * import { getPactById } from 'market-maker.cm';
 *
 * enum REQUEST_EXECUTION_STATUS {
 *   PENDING = 'PENDING',
 *   EXECUTED = 'EXECUTED',
 *   FAILED = 'FAILED',
 * };
 *
 * type ChainData = {
 *   address: string;
 *   nonce: number;
 * };
 *
 * type HistoryAccess = {
 *   historyId: string;
 *   timestamp?: number;
 * };
 *
 * type Pact = {
 *  id: string;
 *  baseAmount: bigint;
 *  promisedL1Amount: bigint;
 *  pactL1Amount: bigint;
 *  minL1Amount: bigint;
 *  collateral: bigint | null;
 *  marketMaker: User;
 *  createdAt: number;
 *  executionStatus: REQUEST_EXECUTION_STATUS;
 *  expirationDate: number;
 *  l1Recipient: string;
 *  requestedOrderId: string | null;
 *  txId: string;
 *  chainData: ChainData;
 *  history: HistoryAccess;
 *  requestInfo: unknown;
 *  executionInfo: {
 *    l1TxId: string;
 *    l1Amount: string;
 *  } | null;
 * };
 *
 * const client: Client = {
 *   fetchClaims: async (firstPart, secondPart) => {
 *     // Implementation using a client library like '@coinweb/wallet-lib' or '@coinweb/claims-client'
 *   }
 * };
 *
 * const pact: Pact = await getPactById(client, 'pact-456');
 * console.log(`PACT ${pact.id} status: ${pact.executionStatus}`);
 * console.log(`L1 recipient: ${pact.l1Recipient}`);
 * console.log(`Requested order ID: ${pact.requestedOrderId}`);
 * console.log(`Transaction ID: ${pact.txId}`);
 * console.log(`Chain data: ${pact.chainData?.address}`);
 * console.log(`Chain data: ${pact.chainData?.nonce}`);
 * console.log(`History: ${pact.history.historyId}`);
 * console.log(`Request info: ${pact.requestInfo}`);
 * console.log(`Execution info: ${pact.executionInfo?.l1TxId}`);
 * console.log(`Execution info: ${pact.executionInfo?.l1Amount}`);
 * ```
 */
export const getPactById = async (client, id) => {
    const key = createRequestKey(id);
    const claimResponse = (await client.fetchClaims(key.first_part, key.second_part))[0];
    if (!claimResponse) {
        throw new Error('Claim not found');
    }
    const data = claimResponse.content.body;
    const { createdAt, executionStatus, expirationDate, l1Recipient, requestedOrderId, txId, chainData, history, requestInfo, executionInfo, marketMaker, } = data;
    return {
        id,
        baseAmount: BigInt(data.baseAmount),
        promisedL1Amount: BigInt(data.promisedL1Amount),
        pactL1Amount: BigInt(data.pactL1Amount),
        minL1Amount: BigInt(data.minL1Amount),
        slippageBips: data.slippageBips,
        collateral: data.collateral ? BigInt(data.collateral) : null,
        createdAt,
        executionStatus,
        expirationDate,
        l1Recipient,
        requestedOrderId,
        txId,
        chainData,
        history,
        requestInfo,
        executionInfo,
        marketMaker,
        isRefund: data.isRefund,
        affiliateFees: data.affiliateFees && {
            ...data.affiliateFees,
            affiliateFeesCweb: BigInt(data.affiliateFees.affiliateFeesCweb),
            affiliateFeesToken: BigInt(data.affiliateFees.affiliateFeesToken),
            affiliateFeesUsdt: data.affiliateFees.affiliateFeesUsdt && BigInt(data.affiliateFees.affiliateFeesUsdt),
        },
    };
};
/**
 * Retrieves all order IDs created by a specific market maker.
 *
 * This function queries the contract's order-by-maker index to find all
 * orders created by the specified market maker. It returns only the order IDs,
 * not the full order data.
 *
 * @param client - The offchain client instance used to fetch claims from the contract.
 *   Must have a `fetchClaims` method that queries the contract's claim storage.
 * @param marketMaker - The market maker whose orders should be retrieved.
 *   Can be a User object with authentication information (e.g., `{ auth: 'EcdsaContract', payload: string }`).
 *
 * @returns A Promise that resolves to an array of order ID strings.
 *
 * @example
 * ```typescript
 * import { type Client } from '@pactswap/cwap-cm-lib/offchain';
 * import { getOrderIdsByMarketMaker } from 'market-maker.cm';
 *
 * const client: Client = {
 *   fetchClaims: async (firstPart, secondPart) => {
 *     // Implementation using a client library like '@coinweb/wallet-lib' or '@coinweb/claims-client'
 *   }
 * };
 *
 * const owner = { auth: 'EcdsaContract', payload: '0x1234...' };
 *
 * // Get all order IDs for a market maker
 * const orderIds = await getOrderIdsByMarketMaker(client, owner);
 * console.log(`Market maker has ${orderIds.length} orders`);
 * ```
 */
export const getOrderIdsByMarketMaker = async (client, marketMaker) => {
    const claimsResponse = await client.fetchClaims(createOrderByMakerIndexFirstPart(marketMaker), null);
    return claimsResponse.map(({ content }) => content.key.second_part[1]);
};
/**
 * Retrieves IDs of active orders created by a specific market maker.
 *
 * This function queries the contract's active-order-by-maker index to find all
 * active orders created by the specified market maker. It returns only the order IDs,
 * not the full order data.
 *
 * @param client - The offchain client instance used to fetch claims from the contract.
 *   Must have a `fetchClaims` method that queries the contract's claim storage.
 * @param marketMaker - The market maker whose orders should be retrieved.
 *   Can be a User object with authentication information (e.g., `{ auth: 'EcdsaContract', payload: string }`).
 *
 * @returns A Promise that resolves to an array of order ID strings.
 *
 * @example
 * ```typescript
 * import { type Client } from '@pactswap/cwap-cm-lib/offchain';
 * import { getActiveOrderIdsByMarketMaker } from 'market-maker.cm';
 *
 * const client: Client = {
 *   fetchClaims: async (firstPart, secondPart) => {
 *     // Implementation using a client library like '@coinweb/wallet-lib' or '@coinweb/claims-client'
 *   }
 * };
 *
 * const owner = { auth: 'EcdsaContract', payload: '0x1234...' };
 *
 * // Get all active order IDs for a market maker
 * const orderIds = await getActiveOrderIdsByMarketMaker(client, owner);
 * console.log(`Market maker has ${orderIds.length} active orders`);
 * ```
 */
export const getActiveOrderIdsByMarketMaker = async (client, marketMaker) => {
    const claimsResponse = await client.fetchClaims(createActiveOrderByMakerIndexFirstPart(marketMaker), null);
    return claimsResponse.map(({ content }) => content.key.second_part[1]);
};
/**
 * @deprecated Use getOrderIdsByMarketMaker instead
 *
 * Retrieves all order IDs owned by a specific market maker.
 *
 * This function queries the contract's order-by-owner index to find all
 * orders created by the specified market maker. It returns only the order IDs,
 * not the full order data.
 *
 * @param client - The offchain client instance used to fetch claims from the contract.
 *   Must have a `fetchClaims` method that queries the contract's claim storage.
 * @param owner - The market maker whose orders should be retrieved.
 *   Can be a User object with authentication information (e.g., `{ auth: 'EcdsaContract', payload: string }`).
 *
 * @returns A Promise that resolves to an array of order ID strings.
 *
 * @example
 * ```typescript
 * import { type Client } from '@pactswap/cwap-cm-lib/offchain';
 * import { getAllOwnOrdersIds } from 'market-maker.cm';
 *
 * const client: Client = {
 *   fetchClaims: async (firstPart, secondPart) => {
 *     // Implementation using a client library like '@coinweb/wallet-lib' or '@coinweb/claims-client'
 *   }
 * };
 *
 * const owner = { auth: 'EcdsaContract', payload: '0x1234...' };
 *
 * // Get all order IDs for a market maker
 * const orderIds = await getAllOwnOrdersIds(client, owner);
 * console.log(`Market maker has ${orderIds.length} orders`);
 * ```
 */
export const getAllOwnOrdersIds = async (client, owner) => getOrderIdsByMarketMaker(client, owner);
/**
 * Retrieves all execution request (PACT) IDs related to a specific market maker.
 *
 * This function queries the contract's request-by-market-maker index to find
 * all execution requests (PACTs) associated with the specified market maker.
 * It returns only the request IDs, not the full request data.
 *
 * @param client - The offchain client instance used to fetch claims from the contract.
 *   Must have a `fetchClaims` method that queries the contract's claim storage.
 * @param marketMaker - The market maker user whose execution requests should be retrieved.
 *   Can be a User object with authentication information (e.g., `{ auth: 'EcdsaContract', payload: pubKey }`).
 *
 * @returns A Promise that resolves to an array of execution request ID strings.
 *
 * @example
 * ```typescript
 * import { type Client } from '@pactswap/cwap-cm-lib/offchain';
 * import { getPactIdsByMarketMaker } from 'market-maker.cm';
 *
 * const client: Client = {
 *   fetchClaims: async (firstPart, secondPart) => {
 *     // Implementation using a client library like '@coinweb/wallet-lib' or '@coinweb/claims-client'
 *   }
 * };
 *
 * const marketMaker = { auth: 'EcdsaContract', payload: '0x1234...' };
 *
 * // Get all PACT IDs for a market maker
 * const pactIds = await getPactIdsByMarketMaker(client, marketMaker);
 * console.log(`Market maker has ${pactIds.length} active PACTs`);
 * ```
 */
export const getPactIdsByMarketMaker = async (client, marketMaker) => {
    const claimsResponse = await client.fetchClaims(createRequestByMarketMakerIndexFirstPart(marketMaker), null);
    return claimsResponse.map(({ content }) => content.key.second_part[1]);
};
/**
 * @deprecated Use getPactIdsByMarketMaker instead
 *
 * Retrieves all execution request (PACT) IDs owned by a specific market maker.
 *
 * This function queries the contract's request-by-market-maker index to find
 * all execution requests (PACTs) associated with the specified market maker.
 * It returns only the request IDs, not the full request data.
 *
 * @param client - The offchain client instance used to fetch claims from the contract.
 *   Must have a `fetchClaims` method that queries the contract's claim storage.
 * @param owner - The market maker user whose execution requests should be retrieved.
 *   Can be a User object with authentication information (e.g., `{ auth: 'EcdsaContract', payload: pubKey }`).
 *
 * @returns A Promise that resolves to an array of execution request ID strings.
 *
 * @example
 * ```typescript
 * import { type Client } from '@pactswap/cwap-cm-lib/offchain';
 * import { getAllOwnClaimIds } from 'market-maker.cm';
 *
 * const client: Client = {
 *   fetchClaims: async (firstPart, secondPart) => {
 *     // Implementation using a client library like '@coinweb/wallet-lib' or '@coinweb/claims-client'
 *   }
 * };
 *
 * const owner = { auth: 'EcdsaContract', payload: '0x1234...' };
 *
 * // Get all PACT IDs for a market maker
 * const pactIds = await getAllOwnPactIds(client, owner);
 * console.log(`Market maker has ${pactIds.length} active PACTs`);
 * ```
 */
export const getAllOwnPactIds = async (client, owner) => getPactIdsByMarketMaker(client, owner);
/**
 * Retrieves all reversed execution request (PACT) IDs across all market makers, ordered by reverse timestamp.
 *
 * This function queries the contract's reverse request index, which contains all execution
 * requests (PACTs) stored in reverse chronological order (most recent first).
 * It returns only the request IDs, not the full request data.
 *
 * @param client - The offchain client instance used to fetch claims from the contract.
 *   Must have a `fetchClaims` method that queries the contract's claim storage.
 *
 * @returns A Promise that resolves to an array of execution request ID strings.
 *
 * @example
 * ```typescript
 * import { type Client } from '@pactswap/cwap-cm-lib/offchain';
 * import { getAllOwnClaimIds } from 'market-maker.cm';
 *
 * const client: Client = {
 *   fetchClaims: async (firstPart, secondPart) => {
 *     // Implementation using a client library like '@coinweb/wallet-lib' or '@coinweb/claims-client'
 *   }
 * };
 *
 * // Get all PACT IDs for a market maker
 * const pactIds = await getAllReversedPactIds(client);
 * console.log(`Market has ${pactIds.length} reversed PACTs`);
 * ```
 */
export const getAllRefundRequestIds = async (client) => {
    const claimsResponse = await client.fetchClaims(createRefundIndexFirstPart(), null);
    return claimsResponse.map(({ content }) => content.key.second_part[1]);
};
/**
 * Retrieves all refund request IDs related to a specific market maker.
 *
 * This function queries the contract's request-by-market-maker index to find
 * all refund requests associated with the specified market maker.
 * It returns only the request IDs, not the full request data.
 *
 * @param client - The offchain client instance used to fetch claims from the contract.
 *   Must have a `fetchClaims` method that queries the contract's claim storage.
 * @param marketMaker - The market maker user whose execution requests should be retrieved.
 *   Can be a User object with authentication information (e.g., `{ auth: 'EcdsaContract', payload: pubKey }`).
 *
 * @returns A Promise that resolves to an array of execution request ID strings.
 *
 * @example
 * ```typescript
 * import { type Client } from '@pactswap/cwap-cm-lib/offchain';
 * import { getRefundRequestIdsByMarketMaker } from 'market-maker.cm';
 *
 * const client: Client = {
 *   fetchClaims: async (firstPart, secondPart) => {
 *     // Implementation using a client library like '@coinweb/wallet-lib' or '@coinweb/claims-client'
 *   }
 * };
 *
 * const marketMaker = { auth: 'EcdsaContract', payload: '0x1234...' };
 *
 * // Get all refund request IDs for a market maker
 * const refundRequestIds = await getRefundRequestIdsByMarketMaker(client, marketMaker);
 * console.log(`Market maker has ${refundRequestIds.length} refund requests`);
 * ```
 */
export const getRefundRequestIdsByMarketMaker = async (client, marketMaker) => {
    const claimsResponse = await client.fetchClaims(createRefundByMarketMakerIndexFirstPart(marketMaker), null);
    return claimsResponse.map(({ content }) => content.key.second_part[1]);
};
/**
 * @deprecated Use getRefundRequestIdsByMarketMaker instead
 *
 * Retrieves all refund request IDs owned by a specific market maker.
 *
 * This function queries the contract's request-by-market-maker index to find
 * all refund requests associated with the specified market maker.
 * It returns only the request IDs, not the full request data.
 *
 * @param client - The offchain client instance used to fetch claims from the contract.
 *   Must have a `fetchClaims` method that queries the contract's claim storage.
 * @param owner - The market maker user whose execution requests should be retrieved.
 *   Can be a User object with authentication information (e.g., `{ auth: 'EcdsaContract', payload: pubKey }`).
 *
 * @returns A Promise that resolves to an array of execution request ID strings.
 *
 * @example
 * ```typescript
 * import { type Client } from '@pactswap/cwap-cm-lib/offchain';
 * import { getMarketMakerRefundRequestIds } from 'market-maker.cm';
 *
 * const client: Client = {
 *   fetchClaims: async (firstPart, secondPart) => {
 *     // Implementation using a client library like '@coinweb/wallet-lib' or '@coinweb/claims-client'
 *   }
 * };
 *
 * const owner = { auth: 'EcdsaContract', payload: '0x1234...' };
 *
 * // Get all refund request IDs for a market maker
 * const refundRequestIds = await getMarketMakerRefundRequestIds(client, owner);
 * console.log(`Market maker has ${refundRequestIds.length} refund requests`);
 * ```
 */
export const getMarketMakerRefundRequestIds = async (client, owner) => getRefundRequestIdsByMarketMaker(client, owner);
/**
 * Retrieves all active order IDs from the contract.
 *
 * This function queries the contract's active order index to find all orders
 * that are currently active (not expired or cancelled). It returns only the
 * order IDs, not the full order data.
 *
 * Active orders are orders that are available for execution by users.
 *
 * @param client - The offchain client instance used to fetch claims from the contract.
 *   Must have a `fetchClaims` method that queries the contract's claim storage.
 *
 * @returns A Promise that resolves to an array of active order ID strings.
 *
 * @example
 * ```typescript
 * import { type Client } from '@pactswap/cwap-cm-lib/offchain';
 * import { getAllActiveOrdersIds } from 'market-maker.cm';
 *
 * const client: Client = {
 *   fetchClaims: async (firstPart, secondPart) => {
 *     // Implementation using a client library like '@coinweb/wallet-lib' or '@coinweb/claims-client'
 *   }
 * };
 *
 * // Get all active order IDs
 * const activeOrderIds = await getAllActiveOrdersIds(client);
 * console.log(`There are ${activeOrderIds.length} active orders`);
 * ```
 */
export const getAllActiveOrdersIds = async (client) => {
    const claimsResponse = await client.fetchClaims(createActiveOrderIndexFirstPart(), null);
    return claimsResponse.map(({ content }) => content.key.second_part[1]);
};
/**
 * @deprecated
 *
 * Retrieves all best by rate order IDs from the contract.
 *
 * This function queries the contract's best order index, which contains orders
 * sorted by their exchange rate (best price). It returns only the order IDs,
 * not the full order data.
 *
 * Best orders are typically sorted by the most favorable exchange rate for
 * users executing swaps.
 *
 * @param client - The offchain client instance used to fetch claims from the contract.
 *   Must have a `fetchClaims` method that queries the contract's claim storage.
 *
 * @returns A Promise that resolves to an array of best order ID strings, sorted
 *   by exchange rate (best first).
 *
 * @example
 * ```typescript
 * import { type Client } from '@pactswap/cwap-cm-lib/offchain';
 * import { getBestOrdersIds } from 'market-maker.cm';
 *
 * const client: Client = {
 *   fetchClaims: async (firstPart, secondPart) => {
 *     // Implementation using a client library like '@coinweb/wallet-lib' or '@coinweb/claims-client'
 *   }
 * };
 *
 * // Get best order IDs (sorted by rate)
 * const bestOrderIds = await getBestOrdersIds(client);
 * console.log(`Best order ID: ${bestOrderIds[0]}`);
 * ```
 */
export const getBestOrdersIds = async (client) => {
    const claimsResponse = await client.fetchClaims(createBestOrderIndexFirstPart(), null);
    return claimsResponse.map(({ content }) => content.key.second_part[1]);
};
/**
 * Retrieves all "best active" order IDs from the contract.
 *
 * This function queries the contract's best active order index, which contains
 * only active orders sorted by their exchange rate (best price). It returns
 * only the order IDs, not the full order data.
 *
 * Best active orders are the most favorable orders that are currently
 * available for execution. This is commonly used in swap interfaces to
 * display the best available rates.
 *
 * @param client - The offchain client instance used to fetch claims from the contract.
 *   Must have a `fetchClaims` method that queries the contract's claim storage.
 *
 * @returns A Promise that resolves to an array of best active order ID strings,
 *   sorted by exchange rate (best first).
 *
 * @example
 * ```typescript
 * import { type Client } from '@pactswap/cwap-cm-lib/offchain';
 * import { getBestActiveOrdersIds } from 'market-maker.cm';
 *
 * const client: Client = {
 *   fetchClaims: async (firstPart, secondPart) => {
 *     // Implementation using a client library like '@coinweb/wallet-lib' or '@coinweb/claims-client'
 *   }
 * };
 *
 * // Get best active order IDs
 * const bestActiveOrderIds = await getBestActiveOrdersIds(client);
 * console.log(`Best active order: ${bestActiveOrderIds[0]}`);
 * ```
 */
export const getBestActiveOrdersIds = async (client) => {
    const claimsResponse = await client.fetchClaims(createBestActiveOrderIndexFirstPart(), null);
    return claimsResponse.map(({ content }) => content.key.second_part[1]);
};
/**
 * Fetches all orders created by a specific market maker.
 *
 * This is a convenience function that combines getAllOwnOrdersIds and
 * getOrderById to retrieve all orders for a market maker with their complete
 * data. It fetches all order IDs first, then fetches each order's full details
 * in parallel.
 *
 * @param client - The offchain client instance used to fetch claims from the contract.
 *   Must have a `fetchClaims` method that queries the contract's claim storage.
 *
 * @param marketMaker - The market maker whose orders should be retrieved.
 *   Can be a User object with authentication information (e.g., `{ auth: 'EcdsaContract', payload: pubKey }`).
 *
 * @returns A Promise that resolves to an array of objects containing
 *   complete order information for all orders owned by the user.
 *
 * @example
 * ```typescript
 * import { type Client } from '@pactswap/cwap-cm-lib/offchain';
 * import { getOrdersByMarketMaker } from 'market-maker.cm';
 *
 * const client: Client = {
 *   fetchClaims: async (firstPart, secondPart) => {
 *     // Implementation using a client library like '@coinweb/wallet-lib' or '@coinweb/claims-client'
 *   }
 * };
 *
 * enum ORDER_ACTIVITY_STATUS {
 *   ACTIVE = 'ACTIVE',
 *   PENDING = 'PENDING',
 *   DISABLED = 'DISABLED',
 * };
 *
 * type User = {
 *   auth: 'EcdsaContract';
 *   payload: string;
 * };
 *
 * type HistoryAccess = {
 *   historyId: string;
 *   timestamp?: number;
 * };
 *
 * type Order = {
 *   id: string;
 *   baseAmount: bigint;
 *   l1Amount: bigint;
 *   minL1Amount: bigint;
 *   collateral: bigint;
 *   covering: bigint;
 *   createdAt: number;
 *   expirationDate: number;
 *   activityStatus: ORDER_ACTIVITY_STATUS;
 *   marketMaker: User;
 *   baseRecipient: User;
 *   txId: string;
 *   history: HistoryAccess;
 * };
 *
 * const marketMaker = { auth: 'EcdsaContract', payload: '0x1234...' };
 *
 * // Get all orders for a market maker
 * const orders: Order[] = await getOrdersByMarketMaker(client, marketMaker);
 * console.log(`Market maker has ${orders.length} orders`);
 * orders.forEach(order => {
 *   console.log(`Order ${order.id}: ${order.baseAmount} base, ${order.l1Amount} L1`);
 * });
 * ```
 */
export const getOrdersByMarketMaker = async (client, marketMaker) => {
    const ids = await getOrderIdsByMarketMaker(client, marketMaker);
    return Promise.all(ids.map((id) => getOrderById(client, id)));
};
/**
 * Fetches active orders created by a specific market maker.
 *
 * This is a convenience function that combines getActiveOrderIdsByMarketMaker and
 * getOrderById to retrieve all active orders for a market maker with their complete
 * data. It fetches all active order IDs first, then fetches each active order's full details
 * in parallel.
 *
 * @param client - The offchain client instance used to fetch claims from the contract.
 *   Must have a `fetchClaims` method that queries the contract's claim storage.
 *
 * @param marketMaker - The market maker whose orders should be retrieved.
 *   Can be a User object with authentication information (e.g., `{ auth: 'EcdsaContract', payload: pubKey }`).
 *
 * @returns A Promise that resolves to an array of objects containing
 *   complete order information for all orders owned by the user.
 *
 * @example
 * ```typescript
 * import { type Client } from '@pactswap/cwap-cm-lib/offchain';
 * import { getActiveOrdersByMarketMaker } from 'market-maker.cm';
 *
 * const client: Client = {
 *   fetchClaims: async (firstPart, secondPart) => {
 *     // Implementation using a client library like '@coinweb/wallet-lib' or '@coinweb/claims-client'
 *   }
 * };
 *
 * enum ORDER_ACTIVITY_STATUS {
 *   ACTIVE = 'ACTIVE',
 *   PENDING = 'PENDING',
 *   DISABLED = 'DISABLED',
 * };
 *
 * type User = {
 *   auth: 'EcdsaContract';
 *   payload: string;
 * };
 *
 * type HistoryAccess = {
 *   historyId: string;
 *   timestamp?: number;
 * };
 *
 * type Order = {
 *   id: string;
 *   baseAmount: bigint;
 *   l1Amount: bigint;
 *   minL1Amount: bigint;
 *   collateral: bigint;
 *   covering: bigint;
 *   createdAt: number;
 *   expirationDate: number;
 *   activityStatus: ORDER_ACTIVITY_STATUS;
 *   marketMaker: User;
 *   baseRecipient: User;
 *   txId: string;
 *   history: HistoryAccess;
 * };
 *
 * const marketMaker = { auth: 'EcdsaContract', payload: '0x1234...' };
 *
 * // Get all orders for a market maker
 * const orders: Order[] = await getActiveOrdersByMarketMaker(client, marketMaker);
 * console.log(`Market maker has ${orders.length} active orders`);
 * orders.forEach(order => {
 *   console.log(`Order ${order.id}: ${order.baseAmount} base, ${order.l1Amount} L1`);
 * });
 * ```
 */
export const getActiveOrdersByMarketMaker = async (client, marketMaker) => {
    const ids = await getActiveOrderIdsByMarketMaker(client, marketMaker);
    return Promise.all(ids.map((id) => getOrderById(client, id)));
};
/**
 * @deprecated Use getOrdersByMarketMaker instead
 *
 * Fetches all complete order objects owned by a specific user (market maker).
 *
 * This is a convenience function that combines getAllOwnOrdersIds and
 * getOrderById to retrieve all orders for a market maker with their complete
 * data. It fetches all order IDs first, then fetches each order's full details
 * in parallel.
 *
 * @param client - The offchain client instance used to fetch claims from the contract.
 *   Must have a `fetchClaims` method that queries the contract's claim storage.
 *
 * @param owner - The user (market maker) whose orders should be retrieved.
 *   Can be a User object with authentication information (e.g., `{ auth: 'EcdsaContract', payload: pubKey }`).
 *
 * @returns A Promise that resolves to an array of objects containing
 *   complete order information for all orders owned by the user.
 *
 * @example
 * ```typescript
 * import { type Client } from '@pactswap/cwap-cm-lib/offchain';
 * import { getAllOwnOrders } from 'market-maker.cm';
 *
 * const client: Client = {
 *   fetchClaims: async (firstPart, secondPart) => {
 *     // Implementation using a client library like '@coinweb/wallet-lib' or '@coinweb/claims-client'
 *   }
 * };
 *
 * enum ORDER_ACTIVITY_STATUS {
 *   ACTIVE = 'ACTIVE',
 *   PENDING = 'PENDING',
 *   DISABLED = 'DISABLED',
 * };
 *
 * type User = {
 *   auth: 'EcdsaContract';
 *   payload: string;
 * };
 *
 * type HistoryAccess = {
 *   historyId: string;
 *   timestamp?: number;
 * };
 *
 * type Order = {
 *   id: string;
 *   baseAmount: bigint;
 *   l1Amount: bigint;
 *   minL1Amount: bigint;
 *   collateral: bigint;
 *   covering: bigint;
 *   createdAt: number;
 *   expirationDate: number;
 *   activityStatus: ORDER_ACTIVITY_STATUS;
 *   marketMaker: User;
 *   baseRecipient: User;
 *   txId: string;
 *   history: HistoryAccess;
 * };
 *
 * const owner = { auth: 'EcdsaContract', payload: '0x1234...' };
 *
 * // Get all orders for a market maker
 * const orders: Order[] = await getAllOwnOrders(client, owner);
 * console.log(`Market maker has ${orders.length} orders`);
 * orders.forEach(order => {
 *   console.log(`Order ${order.id}: ${order.baseAmount} base, ${order.l1Amount} L1`);
 * });
 * ```
 */
export const getAllOwnOrders = async (client, owner) => getOrdersByMarketMaker(client, owner);
/**
 * Fetches all execution requests (PACTs) related to a specific market maker.
 *
 * This is a convenience function that combines getAllOwnClaimIds and
 * getPactById to retrieve all execution requests for a market maker
 * with their complete data. It fetches all request IDs first, then fetches
 * each request's full details in parallel.
 *
 * Execution requests (PACTs) represent user requests to execute swaps against
 * the market maker's orders. This function is used to track all pending and
 * completed swaps for a market maker.
 *
 * @param client - The offchain client instance used to fetch claims from the contract.
 *   Must have a `fetchClaims` method that queries the contract's claim storage.
 *
 * @param marketMaker - The market maker user whose execution requests should be retrieved.
 *   Can be a User object with authentication information (e.g., `{ auth: 'EcdsaContract', payload: string }`).
 *
 * @returns A Promise that resolves to an array of objects containing
 *   containing complete execution request information for all PACTs owned by the market maker.
 *
 * @example
 * ```typescript
 * import { type Client } from '@pactswap/cwap-cm-lib/offchain';
 * import { getPactsByMarketMaker } from 'market-maker.cm';
 *
 * enum REQUEST_EXECUTION_STATUS {
 *   PENDING = 'PENDING',
 *   EXECUTED = 'EXECUTED',
 *   FAILED = 'FAILED',
 * };
 *
 * type ChainData = {
 *   address: string;
 *   nonce: number;
 * };
 *
 * type HistoryAccess = {
 *   historyId: string;
 *   timestamp?: number;
 * };
 *
 * type Pact = {
 *  id: string;
 *  baseAmount: bigint;
 *  promisedL1Amount: bigint;
 *  pactL1Amount: bigint;
 *  minL1Amount: bigint;
 *  collateral: bigint;
 *  createdAt: number;
 *  executionStatus: REQUEST_EXECUTION_STATUS;
 *  expirationDate: number;
 *  l1Recipient: string;
 *  requestedOrderId: string;
 *  txId: string;
 *  chainData: ChainData;
 *  history: HistoryAccess;
 *  requestInfo: unknown;
 *  executionInfo: {
 *    l1TxId: string;
 *    l1Amount: string;
 *  } | null;
 * };
 *
 * const client: Client = {
 *   fetchClaims: async (firstPart, secondPart) => {
 *     // Implementation using a client library like '@coinweb/wallet-lib' or '@coinweb/claims-client'
 *   }
 * };
 *
 * const marketMaker = { auth: 'EcdsaContract', payload: '0x1234...' };
 *
 * // Get all execution requests for a market maker
 * const pacts: Pact[] = await getPactsByMarketMaker(client, marketMaker);
 * console.log(`Market maker has ${pacts.length} active PACTs`);
 * claims.forEach(claim => {
 *   console.log(`PACT ${pacts.id}: Status ${pacts.executionStatus}`);
 * });
 * ```
 */
export const getPactsByMarketMaker = async (client, marketMaker) => {
    const ids = await getPactIdsByMarketMaker(client, marketMaker);
    const claims = await Promise.all(ids.map((id) => getPactById(client, id)));
    return claims;
};
/**
 * @deprecated Use getPactsByMarketMaker instead
 * Fetches all complete execution request (PACT) objects owned by a specific market maker.
 *
 * This is a convenience function that combines getAllOwnClaimIds and
 * getPactById to retrieve all execution requests for a market maker
 * with their complete data. It fetches all request IDs first, then fetches
 * each request's full details in parallel.
 *
 * Execution requests (PACTs) represent user requests to execute swaps against
 * the market maker's orders. This function is used to track all pending and
 * completed swaps for a market maker.
 *
 * @param client - The offchain client instance used to fetch claims from the contract.
 *   Must have a `fetchClaims` method that queries the contract's claim storage.
 *
 * @param owner - The market maker user whose execution requests should be retrieved.
 *   Can be a User object with authentication information (e.g., `{ auth: 'EcdsaContract', payload: string }`).
 *
 * @returns A Promise that resolves to an array of objects containing
 *   containing complete execution request information for all PACTs owned by the market maker.
 *
 * @example
 * ```typescript
 * import { type Client } from '@pactswap/cwap-cm-lib/offchain';
 * import { getAllOwnClaims } from 'market-maker.cm';
 *
 * enum REQUEST_EXECUTION_STATUS {
 *   PENDING = 'PENDING',
 *   EXECUTED = 'EXECUTED',
 *   FAILED = 'FAILED',
 * };
 *
 * type ChainData = {
 *   address: string;
 *   nonce: number;
 * };
 *
 * type HistoryAccess = {
 *   historyId: string;
 *   timestamp?: number;
 * };
 *
 * type Pact = {
 *  id: string;
 *  baseAmount: bigint;
 *  promisedL1Amount: bigint;
 *  pactL1Amount: bigint;
 *  minL1Amount: bigint;
 *  collateral: bigint;
 *  createdAt: number;
 *  executionStatus: REQUEST_EXECUTION_STATUS;
 *  expirationDate: number;
 *  l1Recipient: string;
 *  requestedOrderId: string;
 *  txId: string;
 *  chainData: ChainData;
 *  history: HistoryAccess;
 *  requestInfo: unknown;
 *  executionInfo: {
 *    l1TxId: string;
 *    l1Amount: string;
 *  } | null;
 * };
 *
 * const client: Client = {
 *   fetchClaims: async (firstPart, secondPart) => {
 *     // Implementation using a client library like '@coinweb/wallet-lib' or '@coinweb/claims-client'
 *   }
 * };
 *
 * const owner = { auth: 'EcdsaContract', payload: '0x1234...' };
 *
 * // Get all execution requests for a market maker
 * const pacts: Pact[] = await getAllOwnPacts(client, owner);
 * console.log(`Market maker has ${pacts.length} active PACTs`);
 * claims.forEach(claim => {
 *   console.log(`PACT ${pacts.id}: Status ${pacts.executionStatus}`);
 * });
 * ```
 */
export const getAllOwnPacts = async (client, owner) => getPactsByMarketMaker(client, owner);
/**
 * Fetches all refund request from the contract.
 *
 * This is a convenience function that combines getAllReversedPactIds and
 * getPactById to retrieve all refund requests with their complete data.
 * It fetches all request IDs first, then fetches each request's full details in parallel.
 *
 * Refund requests (PACTs) are refund requests that have been reversed by the contract.
 * This function is used to track all refund requests for a market maker.
 *
 * @param client - The offchain client instance used to fetch claims from the contract.
 *   Must have a `fetchClaims` method that queries the contract's claim storage.
 *
 * @returns A Promise that resolves to an array of objects containing
 *   complete refund request information for all refund requests.
 *
 * @example
 * ```typescript
 * import { type Client } from '@pactswap/cwap-cm-lib/offchain';
 * import { getAllRefundRequests } from 'market-maker.cm';
 *
 * const client: Client = {
 *   fetchClaims: async (firstPart, secondPart) => {
 *     // Implementation using a client library like '@coinweb/wallet-lib' or '@coinweb/claims-client'
 *   }
 * };
 *
 * // Get all reversed execution requests
 * const reversedPacts: Pact[] = await getAllRefundRequests(client);
 * console.log(`Market has ${refundRequests.length} refund requests`);
 * refundRequests.forEach(refundRequest => {
 *   console.log(`Refund request ${refundRequest.id}: Status ${refundRequest.executionStatus}`);
 * });
 * ```
 */
export const getAllRefundRequests = async (client) => {
    const ids = await getAllRefundRequestIds(client);
    const claims = await Promise.all(ids.map((id) => getPactById(client, id)));
    return claims;
};
/**
 * Fetches all refund request objects owned by a specific market maker.
 *
 * This is a convenience function that combines getMarketMakerRefundRequestIds and
 * getPactById to retrieve all refund requests with their complete data.
 * It fetches all request IDs first, then fetches each request's full details in parallel.
 *
 * Refund requests (PACTs) are refund requests that have been reversed by the contract.
 * This function is used to track all refund requests for a market maker.
 *
 * @param client - The offchain client instance used to fetch claims from the contract.
 *   Must have a `fetchClaims` method that queries the contract's claim storage.
 * @param owner - The market maker user whose refund requests should be retrieved.
 *   Can be a User object with authentication information (e.g., `{ auth: 'EcdsaContract', payload: pubKey }`).
 *
 * @returns A Promise that resolves to an array of objects containing
 *   complete refund request information for all refund requests owned by the market maker.
 *
 * @example
 * ```typescript
 * import { type Client } from '@pactswap/cwap-cm-lib/offchain';
 * import { getMarketMakerRefundRequests } from 'market-maker.cm';
 *
 * const client: Client = {
 *   fetchClaims: async (firstPart, secondPart) => {
 *     // Implementation using a client library like '@coinweb/wallet-lib' or '@coinweb/claims-client'
 *   }
 * };
 *
 * const owner = { auth: 'EcdsaContract', payload: '0x1234...' };
 *
 * // Get all refund requests for a market maker
 * const refundRequests: Pact[] = await getMarketMakerRefundRequests(client, owner);
 * console.log(`Market maker has ${refundRequests.length} refund requests`);
 * refundRequests.forEach(refundRequest => {
 *   console.log(`Refund request ${refundRequest.id}: Status ${refundRequest.executionStatus}`);
 * });
 * ```
 */
export const getMarketMakerRefundRequests = async (client, owner) => {
    const ids = await getMarketMakerRefundRequestIds(client, owner);
    return Promise.all(ids.map((id) => getPactById(client, id)));
};
/**
 * Fetches all complete active order objects from the contract.
 *
 * This is a convenience function that combines getAllActiveOrdersIds and
 * getOrderById to retrieve all active orders with their complete data.
 * It fetches all active order IDs first, then fetches each order's full details
 * in parallel.
 *
 * Active orders are orders that are currently available for execution by users.
 * This function is commonly used in swap interfaces to display all available
 * orders.
 *
 * @param client - The offchain client instance used to fetch claims from the contract.
 *   Must have a `fetchClaims` method that queries the contract's claim storage.
 *   and individual order states.
 *
 * @returns A Promise that resolves to an array of objects containing
 *   complete order information for all active orders.
 *
 * @example
 * ```typescript
 * import { type Client } from '@pactswap/cwap-cm-lib/offchain';
 * import { getAllActiveOrders } from 'market-maker.cm';
 *
 * const client: Client = {
 *   fetchClaims: async (firstPart, secondPart) => {
 *     // Implementation using a client library like '@coinweb/wallet-lib' or '@coinweb/claims-client'
 *   }
 * };
 *
 * enum ORDER_ACTIVITY_STATUS {
 *   ACTIVE = 'ACTIVE',
 *   PENDING = 'PENDING',
 *   DISABLED = 'DISABLED',
 * };
 *
 * type User = {
 *   auth: 'EcdsaContract';
 *   payload: string;
 * };
 *
 * type HistoryAccess = {
 *   historyId: string;
 *   timestamp?: number;
 * };
 *
 * type Order = {
 *   id: string;
 *   baseAmount: bigint;
 *   l1Amount: bigint;
 *   minL1Amount: bigint;
 *   collateral: bigint;
 *   covering: bigint;
 *   createdAt: number;
 *   expirationDate: number;
 *   activityStatus: ORDER_ACTIVITY_STATUS;
 *   marketMaker: User;
 *   baseRecipient: User;
 *   txId: string;
 *   history: HistoryAccess;
 * };
 *
 * // Get all active orders
 * const activeOrders: Order[] = await getAllActiveOrders(client);
 * console.log(`There are ${activeOrders.length} active orders available`);
 * activeOrders.forEach(order => {
 *   console.log(`Order ${order.id}: ${order.l1Amount} L1 for ${order.baseAmount} base`);
 * });
 * ```
 */
export const getAllActiveOrders = async (client) => {
    const ids = await getAllActiveOrdersIds(client);
    return Promise.all(ids.map((id) => getOrderById(client, id)));
};
/**
 * Fetches all complete "best" order objects from the contract.
 *
 * This is a convenience function that combines getBestOrdersIds and
 * getOrderById to retrieve all best orders with their complete data.
 * It fetches all best order IDs first (sorted by exchange rate), then fetches
 * each order's full details in parallel.
 *
 * Best orders are sorted by the most favorable exchange rate for users
 * executing swaps. This includes both active and inactive orders.
 *
 * @param client - The offchain client instance used to fetch claims from the contract.
 *   Must have a `fetchClaims` method that queries the contract's claim storage.
 *   and individual order states.
 *
 * @returns A Promise that resolves to an array of objects containing
 *   complete order information for all best orders, sorted by exchange rate (best first).
 *
 * @example
 * ```typescript
 * import { type Client } from '@pactswap/cwap-cm-lib/offchain';
 * import { getBestOrders } from 'market-maker.cm';
 *
 *  enum ORDER_ACTIVITY_STATUS {
 *   ACTIVE = 'ACTIVE',
 *   PENDING = 'PENDING',
 *   DISABLED = 'DISABLED',
 * };
 *
 * type User = {
 *   auth: 'EcdsaContract';
 *   payload: string;
 * };
 *
 * type HistoryAccess = {
 *   historyId: string;
 *   timestamp?: number;
 * };
 *
 * type Order = {
 *   id: string;
 *   baseAmount: bigint;
 *   l1Amount: bigint;
 *   minL1Amount: bigint;
 *   collateral: bigint;
 *   covering: bigint;
 *   createdAt: number;
 *   expirationDate: number;
 *   activityStatus: ORDER_ACTIVITY_STATUS;
 *   marketMaker: User;
 *   baseRecipient: User;
 *   txId: string;
 *   history: HistoryAccess;
 * };
 *
 * const client: Client = {
 *   fetchClaims: async (firstPart, secondPart) => {
 *     // Implementation using a client library like '@coinweb/wallet-lib' or '@coinweb/claims-client'
 *   }
 * };
 *
 * // Get best orders (sorted by rate)
 * const bestOrders: Order[] = await getBestOrders(client);
 * if (bestOrders.length > 0) {
 *   console.log(`Best order: ${bestOrders[0].id} with rate ${bestOrders[0].l1Amount / bestOrders[0].baseAmount}`);
 * }
 * ```
 */
export const getBestOrders = async (client) => {
    const ids = await getBestOrdersIds(client);
    return Promise.all(ids.map((id) => getOrderById(client, id)));
};
/**
 * Fetches all complete "best active" order objects from the contract.
 *
 * This is a convenience function that combines getBestActiveOrdersIds and
 * getOrderById to retrieve all best active orders with their complete data.
 * It fetches all best active order IDs first (sorted by exchange rate), then fetches
 * each order's full details in parallel.
 *
 * Best active orders are the most favorable orders that are currently available
 * for execution. This is the most commonly used function in swap interfaces to
 * display the best available rates to users.
 *
 * @param client - The offchain client instance used to fetch claims from the contract.
 *   Must have a `fetchClaims` method that queries the contract's claim storage.
 *
 * @returns A Promise that resolves to an array of objects containing
 *   complete order information for all best active orders, sorted by exchange rate (best first).
 *
 * @example
 * ```typescript
 * import { type Client } from '@pactswap/cwap-cm-lib/offchain';
 * import { getBestActiveOrders } from 'market-maker.cm';
 *
 * enum ORDER_ACTIVITY_STATUS {
 *   ACTIVE = 'ACTIVE',
 *   PENDING = 'PENDING',
 *   DISABLED = 'DISABLED',
 * };
 *
 * type User = {
 *   auth: 'EcdsaContract';
 *   payload: string;
 * };
 *
 * type HistoryAccess = {
 *   historyId: string;
 *   timestamp?: number;
 * };
 *
 * type Order = {
 *   id: string;
 *   baseAmount: bigint;
 *   l1Amount: bigint;
 *   minL1Amount: bigint;
 *   collateral: bigint;
 *   covering: bigint;
 *   createdAt: number;
 *   expirationDate: number;
 *   activityStatus: ORDER_ACTIVITY_STATUS;
 *   marketMaker: User;
 *   baseRecipient: User;
 *   txId: string;
 *   history: HistoryAccess;
 * };
 *
 * const client: Client = {
 *   fetchClaims: async (firstPart, secondPart, pagination?) => {
 *     // Implementation using a client library like '@coinweb/wallet-lib' or '@coinweb/claims-client'
 *   }
 * };
 *
 * // Get best active orders for swap interface
 * const bestActiveOrders = await getBestActiveOrders(client);
 * console.log(`Best rate: ${bestActiveOrders[0]?.l1Amount / bestActiveOrders[0]?.baseAmount}`);
 * ```
 */
export const getBestActiveOrders = async (client) => {
    const ids = await getBestActiveOrdersIds(client);
    return Promise.all(ids.map((id) => getOrderById(client, id)));
};
/**
 * Retrieves the maker deposit (collateral balance) for a specific market maker.
 *
 * This function fetches the deposit claim that represents the total collateral
 * balance available to a market maker. The balance can be used to create new
 * orders or cover execution requests (PACTs).
 *
 * @param client - The offchain client instance used to fetch claims from the contract.
 *   Must have a `fetchClaims` method that queries the contract's claim storage.
 * @param owner - The user (market maker) whose balance should be retrieved.
 *   Can be a User object with authentication information (e.g., `{ auth: 'EcdsaContract', payload: string }`).
 *
 * @returns A Promise that resolves to a claim object containing the maker deposit
 *   information.
 *
 * @example
 * ```typescript
 * import { type Client } from '@pactswap/cwap-cm-lib/offchain';
 * import { getBalance } from 'market-maker.cm';
 *
 * type BalanceClaim = {
 *   content: {
 *     body: {
 *       owner: User;
 *       history: HistoryAccess;
 *     };
 *     fees_stored: string;
 *   };
 * };
 *
 * const client: Client = {
 *   fetchClaims: async (firstPart, secondPart) => {
 *     // Implementation using a client library like '@coinweb/wallet-lib' or '@coinweb/claims-client'
 *   }
 * };
 *
 * const owner = { auth: 'EcdsaContract', payload: '0x1234...' };
 *
 * // Get maker balance
 * const balanceClaim: BalanceClaim = await getBalance(client, owner);
 * console.log('Balance claim:', balanceClaim);
 * ```
 */
export const getBalance = async (client, owner) => {
    const claimsResponse = await client.fetchClaims(createMakerDepositFirstPart(), [owner]);
    return claimsResponse[0];
};
/**
 * Retrieves all L1 transaction information for a specific market maker.
 *
 * This function fetches all L1 transaction records associated with a market maker.
 * These records contain information about Layer 1 blockchain transactions
 * that are related to the market maker's activity in the market maker contract,
 * including transaction IDs, recipient addresses, amounts, and corresponding
 * L2 transaction IDs.
 *
 * @param client - The offchain client instance used to fetch claims from the contract.
 *   Must have a `fetchClaims` method that queries the contract's claim storage.
 * @param user - The user whose L1 transaction information should be retrieved.
 *   Can be a User object with authentication information (e.g., `{ auth: 'EcdsaContract', payload: pubKey }`).
 *
 * @returns A Promise that resolves to an array of objects containing:
 *   - `l1TxId`: The Layer 1 transaction ID
 *   - `recipientAddress`: The address that received the L1 tokens
 *   - `amount`: The amount transferred (as HexBigInt string)
 *   - `l2TxId`: The corresponding Layer 2 transaction ID
 *
 * @example
 * ```typescript
 * import { type Client } from '@pactswap/cwap-cm-lib/offchain';
 * import { getL1TxInfo } from 'market-maker.cm';
 *
 * const client: Client = {
 *   fetchClaims: async (firstPart, secondPart) => {
 *     // Implementation using a client library like '@coinweb/wallet-lib' or '@coinweb/claims-client'
 *   }
 * };
 *
 * type L1TxInfoData = {
 *   l1TxId: string;
 *   recipientAddress: string;
 *   amount: HexBigInt;
 *   l2TxId: string;
 * };
 *
 * const owner = { auth: 'EcdsaContract', payload: '0x1234...' };
 *
 * // Get all L1 transaction info for a market maker
 * const l1TxInfos: L1TxInfoData[] = await getL1TxInfo(client, owner);
 * console.log(`Market maker has ${l1TxInfos.length} L1 transactions`);
 * l1TxInfos.forEach(tx => {
 *   console.log(`L1 TX ${tx.l1TxId}: ${tx.amount} to ${tx.recipientAddress}`);
 * });
 */
export const getL1TxInfo = async (client, user) => {
    const claimsResponse = await client.fetchClaims(createL1TxInfoFirstPart(user), null);
    return claimsResponse.map((claim) => claim.content.body);
};
